import {AnimatePresence} from 'framer-motion';
import {
  Children,
  ReactNode,
  forwardRef,
  useEffect,
  ReactElement,
  useState,
  cloneElement,
  MouseEventHandler,
  PropsWithChildren,
} from 'react';
import * as Styles from './panel.styles';
import type {PanelItemProps} from './panelItem';

const hidden = {opacity: 0, y: '25%'};

export const Panel = forwardRef<HTMLDivElement, PropsWithChildren<PanelProps>>(
  ({icon, title, isOpen, children, onBlur}, ref) => {
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const listItems = Children.toArray(children);

    const handleContainerClick: MouseEventHandler<HTMLDivElement> = (e) => {
      // Stop propogation of the event when clicking the container. This prevents the event
      // listener attached to the window from firing when a user clicks the container
      e.stopPropagation();
    };

    useEffect(() => {
      const handleKeyPress = (e: KeyboardEvent): void => {
        switch (e.key) {
          case 'ArrowUp':
            setFocusedIndex((prevState) =>
              prevState > 0 ? prevState - 1 : prevState
            );
            break;
          case 'ArrowDown':
            setFocusedIndex((prevState) =>
              prevState < listItems.length - 1 ? prevState + 1 : prevState
            );
            break;
          case 'Esc': // IE/Edge specific
          case 'Escape':
            onBlur();
            break;
          default:
            return;
        }
      };

      if (isOpen) {
        window.addEventListener('keydown', handleKeyPress);
        window.addEventListener('mousedown', onBlur);
        setFocusedIndex(0);
      }

      return () => {
        window.removeEventListener('keydown', handleKeyPress);
        window.removeEventListener('mousedown', onBlur);
        setFocusedIndex(-1);
      };
    }, [isOpen, onBlur, listItems.length, ref]);

    return (
      <AnimatePresence>
        {isOpen && (
          <Styles.Container
            initial={hidden}
            animate={{opacity: 1, y: 0}}
            exit={hidden}
            ref={ref}
            data-testid="panel-container"
            onMouseDown={handleContainerClick}
          >
            {(title || icon) && (
              <Styles.Header data-testid="panel-header">
                {icon}
                {title && <Styles.Title>{title}</Styles.Title>}
              </Styles.Header>
            )}
            <Styles.List>
              {/* Append a `focused` prop to each child in the panel */}
              {listItems.map((el, index) =>
                cloneElement(el as ReactElement<PanelItemProps>, {
                  focused: focusedIndex === index,
                })
              )}
            </Styles.List>
          </Styles.Container>
        )}
      </AnimatePresence>
    );
  }
);

interface PanelProps {
  icon?: ReactNode;
  title?: string;
  isOpen: boolean;
  onBlur: () => void;
}
